import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { ReactNode, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useModal } from '@hooks/useModal';
import { Spacing } from 'chatie-design-system';
import { ArrowLeft, Icon, Setting } from 'chatie-design-system/icon';

import MockDatePickerModal from '@components/modal/MockDatePickerModal';

import {
  HEADER_HEIGHT,
  linearGradient,
  media,
  responsiveMaxWidth,
  safeArea,
} from '@styles/css';
import { isEnv } from '@utils/misc';
import { useWebView } from '@utils/webview';

import Push, { PushSize } from './Push';

const headerWrapperCSS =
  (isTransparent: boolean, backgroundColor?: string) => (theme: Theme) =>
    css`
      position: fixed;
      ${responsiveMaxWidth};
      top: 0;
      z-index: 9;
      width: 100%;
      transition: background-color 0.1s ease-out;
      ${isTransparent
        ? linearGradient('top')
        : !!backgroundColor
        ? css`
            background-color: ${backgroundColor};
          `
        : css`
            background-color: ${theme.var.white};
          `};
      ${safeArea('top')}

      .has-sidebar & {
        ${media.tablet} {
          border-left: 1px solid ${theme.var.gray200};
        }
      }
    `;

const headerCSS =
  (isTransparent: boolean, hideBorder: boolean) => (theme: Theme) =>
    css`
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-left: ${Spacing.small}px;
      padding-right: ${Spacing.medium}px;
      height: ${HEADER_HEIGHT}px;

      ${!isTransparent &&
      !hideBorder &&
      css`
        border-bottom: 1px solid ${theme.border.light};
      `}

      h4 {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        line-height: ${HEADER_HEIGHT}px;
        text-align: center;
        max-width: 70%;

        ${isTransparent &&
        css`
          opacity: 0;
        `}
      }
    `;

const leftCSS = css`
  margin-right: auto;

  > * + * {
    margin-right: ${Spacing.xsmall}px;
  }
`;

const rightCSS = css`
  margin-left: auto;

  > * + * {
    margin-left: ${Spacing.xsmall}px;
  }
`;

const transparentCSS = (fadeInAt: string | number) => css`
  position: absolute;
  ${typeof fadeInAt === 'number'
    ? css`
        top: ${fadeInAt}px;
      `
    : css`
        top: ${fadeInAt};
      `}
  width: 1px;
  height: 1px;
`;

type Props = {
  title?: string | ReactNode;
  help?: ReactNode;
  push?: boolean;
  left?: (color: string) => ReactNode;
  right?: (color: string) => ReactNode;
  transparent?: boolean;
  transparentFadeInAt?: string | number;
  hideBackButton?: boolean;
  hideBorder?: boolean;
  onBack?: () => void;
  isShowDevIcon?: boolean;
  backgroundColor?: string;
  iconColor?: string;
  prev?: string;
};

const Header = ({
  title,
  help,
  push = true,
  left,
  right,
  transparent = false,
  transparentFadeInAt,
  hideBackButton = false,
  hideBorder = false,
  onBack,
  isShowDevIcon = true,
  backgroundColor,
  iconColor,
  prev,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();

  const { safeGoBack } = useWebView();

  const [isTransparent, setTransparent] = useState(transparent);

  const [isShowDatePicker, { open: openModal, close: closeModal }] = useModal(
    'mock-date-picker-modal'
  );

  const { ref, inView } = useInView({ initialInView: transparent });

  const color = isTransparent
    ? theme.true.white
    : !!iconColor
    ? iconColor
    : theme.var.black;

  const isDev = !isEnv('production');

  const goBack = () => {
    safeGoBack(prev);
  };

  useEffect(() => {
    setTransparent(inView);
  }, [router, inView]);

  return (
    <>
      <header css={headerWrapperCSS(isTransparent, backgroundColor)}>
        <div
          css={[
            headerCSS(isTransparent, hideBorder),
            help &&
              css`
                gap: ${Spacing.xsmall}px;
                > h4 {
                  position: static;
                  transform: none;
                  flex: 1;
                  min-width: 0;
                  max-width: none;
                  text-align: left;
                }
                > button,
                > div {
                  flex-shrink: 0;
                }
              `,
          ]}
        >
          {!hideBackButton && (
            <button
              className="icon-btn"
              aria-label={t('뒤로가기')}
              onClick={onBack ?? goBack}
            >
              <Icon icon={ArrowLeft} color={color} />
            </button>
          )}
          {left && <div css={leftCSS}>{left(color)}</div>}
          {isDev && isShowDevIcon && !hideBackButton && (
            <div style={{ display: 'flex' }}>
              <button onClick={openModal}>
                <Icon icon={Setting} />
              </button>
            </div>
          )}
          <h4 className="ellipsis">{title}</h4>

          {help}
          {right && <div css={rightCSS}>{right(color)}</div>}
        </div>
      </header>
      {transparent && transparentFadeInAt && (
        <div css={transparentCSS(transparentFadeInAt)} ref={ref} />
      )}
      {push && <Push top size={PushSize.header} />}
      {isDev && (
        <MockDatePickerModal open={isShowDatePicker} onClose={closeModal} />
      )}
    </>
  );
};

export default Header;
